<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import type { ApiError } from '../types/auth.types'
import LanguageSelector from '../components/LanguageSelector.vue'

const router = useRouter()
const authStore = useAuthStore()
const { t, locale } = useI18n()

// Get current language for URL params
const currentLang = computed(() => locale.value || localStorage.getItem('language') || 'pl')

// Form state
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')
const acceptTerms = ref(false)

// UI state
const loading = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})
const successMessage = ref<string | null>(null)

/**
 * Handle register form submission
 */
const handleRegister = async () => {
  // Reset messages
  error.value = null
  successMessage.value = null
  validationErrors.value = {}

  // Client-side validation
  if (password.value !== passwordConfirm.value) {
    error.value = t('register.passwordsNotMatch')
    return
  }

  if (!acceptTerms.value) {
    error.value = t('register.mustAcceptTerms')
    return
  }

  loading.value = true

  try {
    // Get language from localStorage
    const selectedLanguage = localStorage.getItem('language') || 'pl'

    const response = await authStore.register({
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirm.value,
      language: selectedLanguage
    })

    successMessage.value = t('register.successMessage')

    // Clear form
    email.value = ''
    password.value = ''
    passwordConfirm.value = ''
    acceptTerms.value = false

    // Redirect to login after 3 seconds
    setTimeout(() => {
      router.push('/login')
    }, 3000)
  } catch (err: any) {
    console.error('Register error:', err)

    if (err.response?.data) {
      const apiError = err.response.data as ApiError

      // Handle validation errors
      if (apiError.errors) {
        validationErrors.value = apiError.errors
      }

      // Handle general error message
      if (apiError.message) {
        error.value = apiError.message
      }
    } else {
      error.value = t('register.errorRegister')
    }
  } finally {
    loading.value = false
  }
}

/**
 * Get validation error for field
 */
const getFieldError = (field: string): string | null => {
  return validationErrors.value[field]?.[0] || null
}
</script>

<template>
  <main class="min-h-screen flex items-center justify-center px-4 py-8 bg-gray-50">
    <div class="w-full max-w-md">
      <!-- Language Selector -->
      <div class="mb-4 flex justify-end">
        <LanguageSelector />
      </div>

      <!-- Register Card -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-md p-4">
        <h2 class="text-xl font-semibold text-gray-800 mb-3">{{ t('register.title') }}</h2>
        <p class="text-sm text-gray-600 mb-4">{{ t('register.subtitle') }}</p>

        <!-- Error Alert -->
        <div v-if="error" class="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded">
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>

        <!-- Success Alert -->
        <div v-if="successMessage" class="mb-4 p-3 bg-green-50 border-2 border-green-300 rounded">
          <p class="text-sm text-green-700">{{ successMessage }}</p>
        </div>

        <form @submit.prevent="handleRegister" class="space-y-3">
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">{{ t('register.email') }}</label>
            <input
              type="email"
              id="email"
              v-model="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getFieldError('email') }"
              :placeholder="t('register.emailPlaceholder')"
            />
            <p v-if="getFieldError('email')" class="mt-1 text-sm text-red-600">
              {{ getFieldError('email') }}
            </p>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1">{{ t('register.password') }}</label>
            <input
              type="password"
              id="password"
              v-model="password"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getFieldError('password') }"
              :placeholder="t('register.passwordPlaceholder')"
            />
            <p v-if="getFieldError('password')" class="mt-1 text-sm text-red-600">
              {{ getFieldError('password') }}
            </p>
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="password_confirm" class="block text-sm font-medium text-gray-700 mb-1">{{ t('register.confirmPassword') }}</label>
            <input
              type="password"
              id="password_confirm"
              v-model="passwordConfirm"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600"
              :class="{ 'border-red-500': getFieldError('password_confirmation') }"
              :placeholder="t('register.confirmPasswordPlaceholder')"
            />
            <p v-if="getFieldError('password_confirmation')" class="mt-1 text-sm text-red-600">
              {{ getFieldError('password_confirmation') }}
            </p>
          </div>

          <!-- Terms and Conditions -->
          <div class="flex items-start">
            <input
              type="checkbox"
              id="terms"
              v-model="acceptTerms"
              required
              class="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
            />
            <label for="terms" class="ml-2 text-sm text-gray-600">
              {{ t('register.acceptTerms') }}
              <a :href="`/terms?lang=${currentLang}`" target="_blank" class="text-blue-600 hover:text-blue-700 underline">{{ t('register.appTerms') }}</a>,
              <a :href="`/payment-terms?lang=${currentLang}`" target="_blank" class="text-blue-600 hover:text-blue-700 underline">{{ t('register.paymentTerms') }}</a>
              {{ t('register.and') }}
              <a :href="`/privacy-policy?lang=${currentLang}`" target="_blank" class="text-blue-600 hover:text-blue-700 underline">{{ t('register.privacyPolicy') }}</a>
            </label>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? t('register.registering') : t('register.registerButton') }}
          </button>
        </form>

        <!-- Additional Links -->
        <div class="mt-4 pt-3 border-t border-gray-200">
          <p class="text-sm text-gray-600">
            {{ t('register.hasAccount') }}
            <router-link to="/login" class="text-blue-600 hover:text-blue-700 font-medium">
              {{ t('register.login') }}
            </router-link>
          </p>
        </div>
      </div>
    </div>
  </main>
</template>
